import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  RotateCcw, Plus, Search, X, RefreshCw, Loader2,
  Save, Check, Receipt, ExternalLink, Trash2, FileX,
} from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../api';
import { SidePanel } from '../components/SidePanel';
import { useSociete } from '../context/SocieteContext';

// ─── Constantes ───────────────────────────────────────────────────────────────

const STATUTS_RV = {
  brouillon:    { label: 'Brouillon',    bg: '#F1F5F9', color: '#475569', border: '#CBD5E1' },
  valide:       { label: 'Validé',       bg: '#F0FDF4', color: '#15803D', border: '#BBF7D0' },
  avoir_genere: { label: 'Avoir généré', bg: '#EFF6FF', color: '#1D4ED8', border: '#BFDBFE' },
  annule:       { label: 'Annulé',       bg: '#FEF2F2', color: '#DC2626', border: '#FECACA' },
};

const COUNTS_INIT = { brouillon: 0, valide: 0, avoir_genere: 0, annule: 0 };

function StatutBadge({ statut }) {
  const s = STATUTS_RV[statut] || STATUTS_RV.brouillon;
  return (
    <span style={{
      background: s.bg, color: s.color, border: `1px solid ${s.border}`,
      borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap',
    }}>{s.label}</span>
  );
}

const fmtDate = d => d ? new Date(d).toLocaleDateString('fr-FR') : '—';
const fmtMt   = v => (v != null && v !== '') ? `${parseFloat(v).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €` : '—';
const calcMt  = (qty, pu) => parseFloat((qty * pu).toFixed(4));

// ─── Panneau de détail ─────────────────────────────────────────────────────────

function RetourDetailPanel({ retour: retourInit, onClose, onSaved }) {
  const navigate = useNavigate();
  const [retour, setRetour]         = useState(retourInit);
  const [lignes, setLignes]         = useState(retourInit.lignes || []);
  const [dateRetour, setDateRetour] = useState(retourInit.date_retour?.slice(0, 10) || new Date().toISOString().slice(0, 10));
  const [motif, setMotif]           = useState(retourInit.motif || '');
  const [notes, setNotes]           = useState(retourInit.notes || '');
  const [saving, setSaving]         = useState(false);
  const [validating, setValidating] = useState(false);
  const [avoirLoading, setAvoirLoading]   = useState(false);
  const [annulLoading, setAnnulLoading]   = useState(false);
  const [showAvoirConfirm, setShowAvoirConfirm]     = useState(false);
  const [showAnnulConfirm, setShowAnnulConfirm]     = useState(false);
  const [showValiderConfirm, setShowValiderConfirm] = useState(false);
  // Ajout de ligne
  const [addingLine, setAddingLine] = useState(false);
  const [newLine, setNewLine]       = useState({ designation: '', reference: '', article_id: null, quantite: 1, prix_unitaire_ht: 0, taux_tva: 20 });
  const [artSearch, setArtSearch]   = useState('');
  const [artResults, setArtResults] = useState([]);
  const [artLoading, setArtLoading] = useState(false);

  useEffect(() => {
    setRetour(retourInit);
    setLignes(retourInit.lignes || []);
  }, [retourInit.retour_id, retourInit.statut]); // eslint-disable-line

  // Recherche article (pour ajout de ligne)
  useEffect(() => {
    if (!artSearch || artSearch.length < 2) { setArtResults([]); return; }
    const t = setTimeout(async () => {
      setArtLoading(true);
      try {
        const r = await api.getArticles({ search: artSearch, limit: 8 });
        setArtResults(r.data?.items || r.data || []);
      } finally { setArtLoading(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [artSearch]);

  const readOnly      = retour.statut !== 'brouillon';
  const articleLignes = lignes.filter(l => (l.type_ligne || 'article') === 'article');
  const totalHT       = articleLignes.reduce((s, l) => s + (parseFloat(l.montant_ht) || 0), 0);

  const updateLigne = (idx, field, value) => {
    const next  = [...lignes];
    const newLg = { ...next[idx], [field]: value };
    if (['quantite', 'prix_unitaire_ht'].includes(field)) {
      newLg.montant_ht = calcMt(
        parseFloat(field === 'quantite'         ? value : next[idx].quantite)         || 0,
        parseFloat(field === 'prix_unitaire_ht' ? value : next[idx].prix_unitaire_ht) || 0,
      );
    }
    next[idx] = newLg;
    setLignes(next);
  };

  const removeLigne = (idx) => setLignes(l => l.filter((_, i) => i !== idx));

  const confirmAddLine = () => {
    if (!newLine.designation.trim() && !newLine.article_id) {
      toast.error('La désignation est requise');
      return;
    }
    const qty = parseFloat(newLine.quantite) || 0;
    const pu  = parseFloat(newLine.prix_unitaire_ht) || 0;
    setLignes(prev => [...prev, { ...newLine, ligne_id: null, type_ligne: 'article', montant_ht: calcMt(qty, pu) }]);
    setNewLine({ designation: '', reference: '', article_id: null, quantite: 1, prix_unitaire_ht: 0, taux_tva: 20 });
    setArtSearch('');
    setArtResults([]);
    setAddingLine(false);
  };

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const res = await api.updateRetourVente(retour.retour_id, {
        date_retour: dateRetour,
        motif:       motif || null,
        notes:       notes || null,
        lignes,
      });
      toast.success('Bon de retour enregistré');
      const updated = res.data || res;
      setRetour(updated);
      setLignes(updated.lignes || []);
      onSaved(updated);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur de sauvegarde');
    } finally { setSaving(false); }
  };

  const handleValider = async () => {
    setValidating(true);
    try {
      const res = await api.validerRetourVente(retour.retour_id);
      toast.success('Bon de retour validé — stock remis à jour');
      const updated = res.data || res;
      setRetour(updated);
      onSaved(updated);
      setShowValiderConfirm(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la validation');
    } finally { setValidating(false); }
  };

  const handleAvoir = async () => {
    setAvoirLoading(true);
    try {
      const res = await api.avoirOdooRetour(retour.retour_id);
      const d = res.data || res;
      toast.success(`Avoir ${d.odoo_refund_name || ''} créé dans Odoo`);
      const updated = { ...retour, ...d };
      setRetour(updated);
      onSaved(updated);
      setShowAvoirConfirm(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur Odoo');
    } finally { setAvoirLoading(false); }
  };

  const handleAnnuler = async () => {
    setAnnulLoading(true);
    try {
      await api.annulerRetourVente(retour.retour_id);
      toast.success('Bon de retour annulé');
      const updated = { ...retour, statut: 'annule' };
      setRetour(updated);
      onSaved(updated);
      setShowAnnulConfirm(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'annulation");
    } finally { setAnnulLoading(false); }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>

      {/* Header */}
      <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--text)' }}>{retour.numero}</h2>
              <StatutBadge statut={retour.statut} />
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
              <strong style={{ color: 'var(--text)' }}>{retour.entreprise_nom || '—'}</strong>
              {retour.bl_origine_numero && (
                <>
                  {' · BL '}
                  <span
                    onClick={() => navigate('/bons-livraison-ventes?bl_id=' + retour.bl_origine_id)}
                    style={{ color: 'var(--primary-solid)', cursor: 'pointer', textDecoration: 'underline' }}>
                    {retour.bl_origine_numero}
                  </span>
                </>
              )}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94A3B8', padding: 4 }}>
            <X size={18} />
          </button>
        </div>
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflow: 'auto', padding: '16px 24px' }}>

        {/* Date + Notes */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
          <div>
            <label style={{ fontSize: 11, color: 'var(--text-muted)', display: 'block', marginBottom: 4, fontWeight: 600, textTransform: 'uppercase' }}>Date de retour</label>
            <input type="date" value={dateRetour} disabled={readOnly}
              onChange={e => setDateRetour(e.target.value)}
              style={{ width: '100%', padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 13, background: readOnly ? '#F8FAFC' : '#fff' }} />
          </div>
          <div>
            <label style={{ fontSize: 11, color: 'var(--text-muted)', display: 'block', marginBottom: 4, fontWeight: 600, textTransform: 'uppercase' }}>Notes</label>
            <input type="text" value={notes} disabled={readOnly} placeholder="Remarques…"
              onChange={e => setNotes(e.target.value)}
              style={{ width: '100%', padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 13, background: readOnly ? '#F8FAFC' : '#fff' }} />
          </div>
        </div>

        {/* Motif */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 11, color: 'var(--text-muted)', display: 'block', marginBottom: 4, fontWeight: 600, textTransform: 'uppercase' }}>Motif du retour</label>
          <textarea value={motif} disabled={readOnly}
            placeholder="Mauvaise pièce, article défectueux, erreur de commande…"
            onChange={e => setMotif(e.target.value)} rows={2}
            style={{ width: '100%', padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 13, resize: 'vertical', fontFamily: 'inherit', background: readOnly ? '#F8FAFC' : '#fff', boxSizing: 'border-box' }} />
        </div>

        {/* Tableau lignes */}
        <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead style={{ background: '#FAFAFA' }}>
              <tr>
                {['Article', 'Qté retour', 'PU HT', 'Montant HT', ''].map((h, i) => (
                  <th key={i} style={{ padding: '8px 10px', textAlign: i > 0 ? 'right' : 'left', fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {articleLignes.length === 0 && !addingLine ? (
                <tr>
                  <td colSpan={5} style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
                    Aucune ligne — {readOnly ? 'retour sans articles' : 'ajoutez des articles à retourner'}
                  </td>
                </tr>
              ) : articleLignes.map((lg, idx) => {
                const globalIdx = lignes.indexOf(lg);
                const nbDec    = lg.nb_decimales ?? 2;
                const needsQty = !readOnly && parseFloat(lg.quantite) <= 0;
                return (
                  <tr key={lg.ligne_id || idx} style={{ borderTop: '1px solid var(--border-light)', background: needsQty ? '#FFFBEB' : undefined }}>
                    <td style={{ padding: '8px 10px' }}>
                      {lg.reference && (
                        <span style={{ fontFamily: 'monospace', color: '#94A3B8', marginRight: 6, fontSize: 11 }}>{lg.reference}</span>
                      )}
                      <span style={{ color: 'var(--text)' }}>{lg.designation || '—'}</span>
                      {needsQty && <div style={{ fontSize: 10, color: '#B45309', marginTop: 2 }}>Quantité à saisir</div>}
                    </td>
                    <td style={{ padding: '8px 10px', textAlign: 'right' }}>
                      {readOnly ? (
                        <span style={{ fontWeight: 600, color: 'var(--text)' }}>{parseFloat(lg.quantite || 0)}</span>
                      ) : (
                        <input
                          data-testid={`rv-qte-${lg.ligne_id || idx}`}
                          type="number" min={0}
                          step={nbDec === 0 ? 1 : nbDec > 0 ? Math.pow(10, -nbDec) : 'any'}
                          value={lg.quantite ?? ''}
                          onChange={e => updateLigne(globalIdx, 'quantite', e.target.value === '' ? 0 : Number(e.target.value))}
                          style={{ width: 70, padding: '3px 6px', border: `1px solid ${needsQty ? '#FCD34D' : 'var(--border)'}`, borderRadius: 4, textAlign: 'right', fontSize: 13 }}
                        />
                      )}
                    </td>
                    <td style={{ padding: '8px 10px', textAlign: 'right' }}>
                      {readOnly ? (
                        <span style={{ color: 'var(--text-secondary)' }}>{parseFloat(lg.prix_unitaire_ht || 0).toFixed(2)}</span>
                      ) : (
                        <input
                          data-testid={`rv-pu-${lg.ligne_id || idx}`}
                          type="number" min={0} step="0.01"
                          value={lg.prix_unitaire_ht ?? ''}
                          onChange={e => updateLigne(globalIdx, 'prix_unitaire_ht', e.target.value === '' ? 0 : Number(e.target.value))}
                          style={{ width: 80, padding: '3px 6px', border: '1px solid var(--border)', borderRadius: 4, textAlign: 'right', fontSize: 13 }}
                        />
                      )}
                    </td>
                    <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 600, color: 'var(--text)' }}>
                      {fmtMt(lg.montant_ht)}
                    </td>
                    <td style={{ padding: '8px 6px', textAlign: 'center', width: 32 }}>
                      {!readOnly && (
                        <button onClick={() => removeLigne(globalIdx)} data-testid={`rv-del-line-${idx}`}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#EF4444', padding: 4 }}>
                          <Trash2 size={13} />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}

              {/* Formulaire nouvelle ligne */}
              {addingLine && (
                <tr style={{ borderTop: '1px solid var(--border-light)', background: '#F0F9FF' }}>
                  <td style={{ padding: '8px 10px' }}>
                    <div style={{ position: 'relative' }}>
                      <input
                        data-testid="rv-new-line-designation"
                        placeholder="Désignation ou référence article…"
                        value={artSearch}
                        onChange={e => { setArtSearch(e.target.value); setNewLine(p => ({ ...p, designation: e.target.value, article_id: null })); }}
                        autoFocus
                        style={{ width: '100%', padding: '4px 8px', border: '1px solid #3B82F6', borderRadius: 5, fontSize: 12, boxSizing: 'border-box' }}
                      />
                      {artLoading && <Loader2 size={11} className="animate-spin" style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', color: '#3B82F6' }} />}
                      {artResults.length > 0 && (
                        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50, background: '#fff', border: '1px solid #BFDBFE', borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,.1)', maxHeight: 160, overflowY: 'auto' }}>
                          {artResults.map(a => (
                            <div key={a.article_id}
                              onClick={() => {
                                setNewLine(p => ({ ...p, article_id: a.article_id, reference: a.reference || '', designation: a.designation, prix_unitaire_ht: parseFloat(a.prix_vente || 0) }));
                                setArtSearch(a.designation);
                                setArtResults([]);
                              }}
                              style={{ padding: '6px 10px', fontSize: 12, cursor: 'pointer', borderBottom: '1px solid #EFF6FF' }}
                              onMouseEnter={e => e.currentTarget.style.background = '#EFF6FF'}
                              onMouseLeave={e => e.currentTarget.style.background = ''}>
                              {a.reference && <span style={{ color: '#94A3B8', fontFamily: 'monospace', marginRight: 8, fontSize: 11 }}>{a.reference}</span>}
                              {a.designation}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </td>
                  <td style={{ padding: '8px 10px', textAlign: 'right' }}>
                    <input type="number" min={0} step="any" value={newLine.quantite}
                      data-testid="rv-new-line-qte"
                      onChange={e => setNewLine(p => ({ ...p, quantite: Number(e.target.value) || 0 }))}
                      style={{ width: 70, padding: '3px 6px', border: '1px solid #BFDBFE', borderRadius: 4, textAlign: 'right', fontSize: 13 }} />
                  </td>
                  <td style={{ padding: '8px 10px', textAlign: 'right' }}>
                    <input type="number" min={0} step="0.01" value={newLine.prix_unitaire_ht}
                      data-testid="rv-new-line-pu"
                      onChange={e => setNewLine(p => ({ ...p, prix_unitaire_ht: Number(e.target.value) || 0 }))}
                      style={{ width: 80, padding: '3px 6px', border: '1px solid #BFDBFE', borderRadius: 4, textAlign: 'right', fontSize: 13 }} />
                  </td>
                  <td style={{ padding: '8px 10px', textAlign: 'right', fontSize: 12, color: '#64748B' }}>
                    {fmtMt(calcMt(newLine.quantite, newLine.prix_unitaire_ht))}
                  </td>
                  <td style={{ padding: '8px 6px' }}>
                    <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                      <button onClick={confirmAddLine} data-testid="rv-confirm-add-line"
                        style={{ background: '#22C55E', color: '#fff', border: 'none', borderRadius: 4, padding: '4px 10px', fontSize: 12, cursor: 'pointer', fontWeight: 700 }}>
                        +
                      </button>
                      <button onClick={() => { setAddingLine(false); setArtSearch(''); setArtResults([]); }}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94A3B8', padding: 2 }}>
                        <X size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Total + Ajouter ligne */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: 12 }}>
          <div>
            {!readOnly && !addingLine && (
              <button onClick={() => setAddingLine(true)} data-testid="rv-add-line"
                style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: '1px dashed #CBD5E1', borderRadius: 6, padding: '5px 12px', fontSize: 12, color: '#64748B', cursor: 'pointer', fontWeight: 500 }}>
                <Plus size={13} />
                Ajouter une ligne
              </button>
            )}
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', marginBottom: 2 }}>Total HT</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--text)' }}>{fmtMt(totalHT)}</div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div style={{ borderTop: '1px solid var(--border)', padding: '12px 24px', flexShrink: 0 }}>

        {/* Confirmation : Valider */}
        {showValiderConfirm && (
          <div style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 8, padding: '12px 14px', marginBottom: 10 }}>
            <p style={{ margin: '0 0 6px', fontSize: 13, fontWeight: 600, color: '#15803D' }}>Confirmer la validation ?</p>
            <p style={{ margin: '0 0 10px', fontSize: 12, color: '#166534' }}>
              Le stock sera remis à jour. Les matériels seront replacés en «stock». Action irréversible.
            </p>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setShowValiderConfirm(false)}
                style={{ flex: 1, padding: '7px 0', borderRadius: 7, border: '1px solid #BBF7D0', background: '#fff', fontSize: 12, cursor: 'pointer', color: '#15803D' }}>
                Annuler
              </button>
              <button data-testid="btn-confirm-valider" onClick={handleValider} disabled={validating}
                style={{ flex: 2, padding: '7px 0', borderRadius: 7, background: '#15803D', color: '#fff', border: 'none', fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                {validating ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                Valider le retour
              </button>
            </div>
          </div>
        )}

        {/* Confirmation : Avoir Odoo */}
        {showAvoirConfirm && (
          <div style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 8, padding: '12px 14px', marginBottom: 10 }}>
            <p style={{ margin: '0 0 6px', fontSize: 13, fontWeight: 600, color: '#1D4ED8' }}>Générer un avoir client Odoo ?</p>
            <p style={{ margin: '0 0 10px', fontSize: 12, color: '#1e40af' }}>
              Client : <strong>{retour.entreprise_nom}</strong> · BRV : <strong>{retour.numero}</strong>
            </p>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setShowAvoirConfirm(false)}
                style={{ flex: 1, padding: '7px 0', borderRadius: 7, border: '1px solid #BFDBFE', background: '#fff', fontSize: 12, cursor: 'pointer', color: '#1D4ED8' }}>
                Annuler
              </button>
              <button data-testid="btn-confirm-avoir" onClick={handleAvoir} disabled={avoirLoading}
                style={{ flex: 2, padding: '7px 0', borderRadius: 7, background: '#1D4ED8', color: '#fff', border: 'none', fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                {avoirLoading ? <Loader2 size={12} className="animate-spin" /> : <Receipt size={12} />}
                Générer l'avoir Odoo
              </button>
            </div>
          </div>
        )}

        {/* Confirmation : Annuler */}
        {showAnnulConfirm && (
          <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, padding: '12px 14px', marginBottom: 10 }}>
            <p style={{ margin: '0 0 6px', fontSize: 13, fontWeight: 600, color: '#DC2626' }}>Annuler ce bon de retour ?</p>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setShowAnnulConfirm(false)}
                style={{ flex: 1, padding: '7px 0', borderRadius: 7, border: '1px solid #FECACA', background: '#fff', fontSize: 12, cursor: 'pointer', color: '#DC2626' }}>
                Non
              </button>
              <button data-testid="btn-confirm-annuler" onClick={handleAnnuler} disabled={annulLoading}
                style={{ flex: 2, padding: '7px 0', borderRadius: 7, background: '#DC2626', color: '#fff', border: 'none', fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                {annulLoading ? <Loader2 size={12} className="animate-spin" /> : <FileX size={12} />}
                Confirmer l'annulation
              </button>
            </div>
          </div>
        )}

        {/* Badge avoir existant */}
        {retour.odoo_refund_name && (
          <div style={{ marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8, background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 8, padding: '8px 12px' }}>
            <Receipt size={14} style={{ color: '#1D4ED8', flexShrink: 0 }} />
            <span style={{ fontSize: 12, color: '#1D4ED8', fontWeight: 600 }}>
              Avoir Odoo — {retour.odoo_refund_name}
            </span>
            {retour.odoo_refund_id && (
              <a href={`${process.env.REACT_APP_ODOO_URL}/odoo/accounting/credit-notes/${retour.odoo_refund_id}`}
                target="_blank" rel="noreferrer" style={{ marginLeft: 'auto', color: '#1D4ED8' }}>
                <ExternalLink size={12} />
              </a>
            )}
          </div>
        )}

        {/* Boutons d'action */}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          {retour.statut === 'brouillon' && !showValiderConfirm && !showAnnulConfirm && (
            <button onClick={() => setShowAnnulConfirm(true)} data-testid="btn-annuler-retour"
              style={{ padding: '7px 14px', borderRadius: 8, border: '1px solid #FECACA', background: '#fff', fontSize: 12, cursor: 'pointer', color: '#DC2626', fontWeight: 600 }}>
              Annuler le BRV
            </button>
          )}
          {retour.statut === 'brouillon' && !showAnnulConfirm && (
            <>
              <button onClick={handleSave} disabled={saving} data-testid="btn-save-retour"
                className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border transition-all hover:bg-gray-50"
                style={{ borderColor: 'var(--border)', color: 'var(--text-secondary)', background: '#fff' }}>
                {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                Enregistrer
              </button>
              {!showValiderConfirm && (
                <button onClick={() => setShowValiderConfirm(true)} data-testid="btn-valider-retour"
                  style={{ padding: '7px 18px', borderRadius: 8, background: '#15803D', color: '#fff', border: 'none', fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Check size={13} />
                  Valider
                </button>
              )}
            </>
          )}
          {retour.statut === 'valide' && !retour.odoo_refund_id && !showAvoirConfirm && (
            <button onClick={() => setShowAvoirConfirm(true)} data-testid="btn-avoir-odoo"
              style={{ padding: '7px 18px', borderRadius: 8, background: '#1a1a2e', color: '#fff', border: 'none', fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 7 }}>
              <Receipt size={13} />
              Générer l'avoir Odoo
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Modal création ────────────────────────────────────────────────────────────

function NouveauRetourModal({ onClose, onCreated }) {
  const [mode, setMode]           = useState('bl');
  const [blSearch, setBlSearch]   = useState('');
  const [blResults, setBlResults] = useState([]);
  const [blSelected, setBlSelected] = useState(null);
  const [blLoading, setBlLoading] = useState(false);
  const [clientSearch, setClientSearch]     = useState('');
  const [clientResults, setClientResults]   = useState([]);
  const [clientSelected, setClientSelected] = useState(null);
  const [clientLoading, setClientLoading]   = useState(false);
  const [dateRetour, setDateRetour] = useState(new Date().toISOString().slice(0, 10));
  const [motif, setMotif]           = useState('');
  const [creating, setCreating]     = useState(false);

  // Recherche BL (debounced)
  useEffect(() => {
    if (!blSearch || blSearch.length < 2) { setBlResults([]); return; }
    const t = setTimeout(async () => {
      setBlLoading(true);
      try {
        const r = await api.getBLVentes({ search: blSearch });
        setBlResults(r.data?.data || []);
      } finally { setBlLoading(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [blSearch]);

  // Recherche client (debounced)
  useEffect(() => {
    if (!clientSearch || clientSearch.length < 2) { setClientResults([]); return; }
    const t = setTimeout(async () => {
      setClientLoading(true);
      try {
        const r = await api.getEntreprises({ search: clientSearch, limit: 8 });
        setClientResults(r.data?.items || r.data || []);
      } finally { setClientLoading(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [clientSearch]);

  const canCreate = mode === 'bl' ? !!blSelected : !!clientSelected;

  const handleCreate = async () => {
    if (!canCreate) return;
    setCreating(true);
    try {
      const payload = {
        date_retour: dateRetour,
        motif:       motif || null,
        ...(mode === 'bl'
          ? { bl_origine_id: blSelected.bl_id }
          : { entreprise_id: clientSelected.entreprise_id, entreprise_nom: clientSelected.nom }),
      };
      const res = await api.creerRetourVente(payload);
      toast.success('Bon de retour créé');
      onCreated(res.data || res);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la création');
    } finally { setCreating(false); }
  };

  const switchMode = (m) => {
    setMode(m);
    setBlSelected(null); setBlSearch(''); setBlResults([]);
    setClientSelected(null); setClientSearch(''); setClientResults([]);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: '#fff', borderRadius: 16, padding: 28, width: '100%', maxWidth: 500, boxShadow: '0 20px 60px rgba(0,0,0,0.18)' }}>

        {/* En-tête */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <RotateCcw size={18} style={{ color: '#1a1a2e' }} />
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--text)' }}>Nouveau bon de retour</h2>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94A3B8', padding: 4 }}>
            <X size={18} />
          </button>
        </div>

        {/* Sélecteur de mode */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
          {[{ key: 'bl', label: 'Depuis un BL livré' }, { key: 'libre', label: 'Retour libre' }].map(m => (
            <button key={m.key} onClick={() => switchMode(m.key)}
              data-testid={`modal-mode-${m.key}`}
              style={{ flex: 1, padding: '8px 0', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer',
                background: mode === m.key ? '#1a1a2e' : '#F8FAFC',
                color:      mode === m.key ? '#fff' : 'var(--text-secondary)',
                border:     mode === m.key ? 'none' : '1px solid var(--border)' }}>
              {m.label}
            </button>
          ))}
        </div>

        {/* Recherche BL */}
        {mode === 'bl' && (
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>BL d'origine</label>
            {blSelected ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 8, padding: '8px 12px' }}>
                <span style={{ flex: 1, fontSize: 13 }}>
                  <strong style={{ fontFamily: 'monospace' }}>{blSelected.numero}</strong>
                  <span style={{ color: '#64748B', marginLeft: 8 }}>{blSelected.entreprise_nom}</span>
                </span>
                <button onClick={() => { setBlSelected(null); setBlSearch(''); }}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94A3B8', padding: 2 }}>
                  <X size={13} />
                </button>
              </div>
            ) : (
              <div style={{ position: 'relative' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px', background: '#fff' }}>
                  <Search size={13} style={{ color: '#94A3B8', flexShrink: 0 }} />
                  <input data-testid="modal-bl-search"
                    value={blSearch} onChange={e => setBlSearch(e.target.value)}
                    placeholder="N° BL ou nom client…"
                    style={{ flex: 1, border: 'none', outline: 'none', fontSize: 13 }} />
                  {blLoading && <Loader2 size={13} className="animate-spin" style={{ color: '#94A3B8' }} />}
                </div>
                {blResults.length > 0 && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50, background: '#fff', border: '1px solid var(--border)', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,.1)', maxHeight: 200, overflowY: 'auto', marginTop: 2 }}>
                    {blResults.map(b => (
                      <div key={b.bl_id} onClick={() => { setBlSelected(b); setBlSearch(''); setBlResults([]); }}
                        style={{ padding: '9px 14px', fontSize: 13, cursor: 'pointer', borderBottom: '1px solid #F1F5F9', display: 'flex', gap: 12, alignItems: 'center' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#F8FAFC'}
                        onMouseLeave={e => e.currentTarget.style.background = ''}>
                        <span style={{ fontFamily: 'monospace', color: '#1a1a2e', fontWeight: 600, flexShrink: 0 }}>{b.numero}</span>
                        <span style={{ color: '#64748B', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.entreprise_nom}</span>
                        <span style={{ color: '#94A3B8', fontSize: 11, flexShrink: 0 }}>{fmtDate(b.date_livraison)}</span>
                      </div>
                    ))}
                  </div>
                )}
                {blSearch.length >= 2 && !blLoading && blResults.length === 0 && (
                  <p style={{ marginTop: 4, fontSize: 11, color: '#94A3B8' }}>Aucun BL trouvé</p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Recherche client (mode libre) */}
        {mode === 'libre' && (
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>Client</label>
            {clientSelected ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#F0F9FF', border: '1px solid #BAE6FD', borderRadius: 8, padding: '8px 12px' }}>
                <span style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>{clientSelected.nom}</span>
                <button onClick={() => { setClientSelected(null); setClientSearch(''); }}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94A3B8', padding: 2 }}>
                  <X size={13} />
                </button>
              </div>
            ) : (
              <div style={{ position: 'relative' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px', background: '#fff' }}>
                  <Search size={13} style={{ color: '#94A3B8', flexShrink: 0 }} />
                  <input data-testid="modal-client-search"
                    value={clientSearch} onChange={e => setClientSearch(e.target.value)}
                    placeholder="Nom du client…"
                    style={{ flex: 1, border: 'none', outline: 'none', fontSize: 13 }} />
                  {clientLoading && <Loader2 size={13} className="animate-spin" style={{ color: '#94A3B8' }} />}
                </div>
                {clientResults.length > 0 && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50, background: '#fff', border: '1px solid var(--border)', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,.1)', maxHeight: 180, overflowY: 'auto', marginTop: 2 }}>
                    {clientResults.map(c => (
                      <div key={c.entreprise_id} onClick={() => { setClientSelected(c); setClientSearch(''); setClientResults([]); }}
                        style={{ padding: '9px 14px', fontSize: 13, cursor: 'pointer', borderBottom: '1px solid #F1F5F9' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#F8FAFC'}
                        onMouseLeave={e => e.currentTarget.style.background = ''}>
                        {c.nom}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Date + Motif */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
          <div>
            <label style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>Date de retour</label>
            <input type="date" value={dateRetour} onChange={e => setDateRetour(e.target.value)}
              style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 13 }} />
          </div>
        </div>

        <div style={{ marginBottom: 20 }}>
          <label style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>Motif (optionnel)</label>
          <textarea value={motif} onChange={e => setMotif(e.target.value)} rows={2}
            placeholder="Mauvaise pièce, article défectueux…"
            style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 13, resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box' }} />
        </div>

        {/* Footer modal */}
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onClose}
            style={{ flex: 1, padding: '9px 0', borderRadius: 9, border: '1px solid var(--border)', background: '#fff', fontSize: 13, cursor: 'pointer', color: 'var(--text-secondary)', fontWeight: 600 }}>
            Annuler
          </button>
          <button onClick={handleCreate} disabled={creating || !canCreate}
            data-testid="btn-creer-retour"
            style={{ flex: 2, padding: '9px 0', borderRadius: 9, background: (canCreate && !creating) ? '#1a1a2e' : '#CBD5E1', color: '#fff', border: 'none', fontSize: 13, fontWeight: 700, cursor: canCreate ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            {creating ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            Créer le bon de retour
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Page principale ───────────────────────────────────────────────────────────

export default function RetoursVentes() {
  const { activeAgences, isReadOnly } = useSociete();
  const [data, setData]       = useState([]);
  const [total, setTotal]     = useState(0);
  const [loading, setLoading] = useState(true);
  const [counts, setCounts]   = useState(COUNTS_INIT);
  const [filters, setFilters] = useState({ statut: '' });
  const [searchInput, setSearchInput] = useState('');
  const [panel, setPanel]     = useState(null);
  const [showModal, setShowModal] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.statut) params.statut = filters.statut;
      const [r, ...countRes] = await Promise.all([
        api.getRetoursVentes(params),
        ...Object.keys(COUNTS_INIT).map(s => api.getRetoursVentes({ statut: s })),
      ]);
      setData(r.data?.data || []);
      setTotal(r.data?.total || 0);
      const keys = Object.keys(COUNTS_INIT);
      setCounts(Object.fromEntries(keys.map((k, i) => [k, countRes[i].data?.total || 0])));
    } catch {
      toast.error('Erreur chargement des bons de retour');
    } finally { setLoading(false); }
  }, [filters, activeAgences]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const handleSaved = (updated) => {
    setData(prev => prev.map(r => r.retour_id === updated.retour_id ? updated : r));
    setPanel({ retour: updated });
    load();
  };

  const handleCreated = (newRetour) => {
    setShowModal(false);
    setData(prev => [newRetour, ...prev]);
    setPanel({ retour: newRetour });
    load();
  };

  const handleStatutFilter = (s) => setFilters(f => ({ ...f, statut: f.statut === s ? '' : s }));

  const displayedData = searchInput.trim()
    ? data.filter(r =>
        (r.numero || '').toLowerCase().includes(searchInput.toLowerCase()) ||
        (r.entreprise_nom || '').toLowerCase().includes(searchInput.toLowerCase()) ||
        (r.bl_origine_numero || '').toLowerCase().includes(searchInput.toLowerCase())
      )
    : data;

  return (
    <div className="min-h-screen p-6 space-y-6" style={{ background: 'var(--background)' }}>

      {/* En-tête */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--text)' }}>Bons de retour</h1>
          <p className="text-sm mt-0.5" style={{ color: 'var(--text-muted)' }}>Retours clients et avoirs</p>
        </div>
        <button data-testid="btn-nouveau-retour" onClick={() => setShowModal(true)}
          disabled={isReadOnly}
          title={isReadOnly ? 'Sélectionnez une agence unique pour créer un retour' : ''}
          className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold text-white transition-all hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
          style={{ background: '#1a1a2e' }}>
          <Plus size={14} />
          Nouveau retour
        </button>
      </div>

      {/* Stats par statut */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {Object.entries(COUNTS_INIT).map(([key]) => {
          const s = STATUTS_RV[key];
          return (
            <button key={key} onClick={() => handleStatutFilter(key)}
              data-testid={`rv-stat-${key}`}
              className="rounded-xl p-4 text-left border transition-all hover:shadow-md"
              style={{ background: filters.statut === key ? s.bg : 'var(--surface)', borderColor: filters.statut === key ? s.border : 'var(--border)' }}>
              <div className="text-2xl font-bold" style={{ color: s.color }}>{counts[key]}</div>
              <div className="text-xs font-medium mt-0.5" style={{ color: 'var(--text-secondary)' }}>{s.label}</div>
            </button>
          );
        })}
      </div>

      {/* Barre de recherche */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 px-3 py-2 rounded-xl border bg-white flex-1 min-w-48" style={{ borderColor: 'var(--border)' }}>
          <Search size={14} style={{ color: 'var(--text-muted)' }} />
          <input value={searchInput} onChange={e => setSearchInput(e.target.value)}
            placeholder="N° BRV, client, BL d'origine…"
            className="flex-1 text-sm focus:outline-none" style={{ color: 'var(--text)', background: 'transparent' }}
            data-testid="rv-search-input" />
          {searchInput && (
            <button onClick={() => setSearchInput('')}>
              <X size={12} style={{ color: 'var(--text-muted)' }} />
            </button>
          )}
        </div>
        <button onClick={load} className="p-2 rounded-xl border hover:bg-gray-50 transition-colors"
          style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }} data-testid="rv-refresh">
          <RefreshCw size={14} />
        </button>
        <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{total} BRV</span>
      </div>

      {/* Tableau */}
      {loading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="animate-spin" size={24} style={{ color: 'var(--primary-solid)' }} />
        </div>
      ) : (
        <div className="bg-white rounded-2xl border overflow-hidden" style={{ borderColor: 'var(--border)' }}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <colgroup>
                <col style={{ width: 120 }} />
                <col style={{ width: 160 }} />
                <col style={{ width: 130 }} />
                <col style={{ width: 100 }} />
                <col style={{ width: 180 }} />
                <col style={{ width: 110 }} />
                <col style={{ width: 90 }} />
                <col style={{ width: 120 }} />
                <col style={{ width: 70 }} />
              </colgroup>
              <thead>
                <tr style={{ background: '#FAFAFA', borderBottom: '1px solid var(--border)' }}>
                  {["N° BRV", "Client", "BL d'origine", "Date retour", "Motif", "Total HT", "Statut", "Avoir Odoo", "Suivi par"].map(h => (
                    <th key={h} className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wide whitespace-nowrap"
                      style={{ color: 'var(--text-muted)' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {displayedData.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-5 py-16 text-center text-sm" style={{ color: 'var(--text-muted)' }}>
                      Aucun bon de retour — cliquez sur «Nouveau retour» pour commencer
                    </td>
                  </tr>
                ) : displayedData.map((rv, i) => (
                  <tr key={rv.retour_id}
                    data-testid={`rv-row-${rv.retour_id}`}
                    onClick={() => setPanel({ retour: rv })}
                    className="cursor-pointer transition-colors hover:bg-blue-50/40"
                    style={{ borderBottom: i < displayedData.length - 1 ? '1px solid var(--border-light)' : 'none' }}>
                    <td className="px-5 py-3 text-xs" style={{ color: 'var(--text)', fontFamily: 'monospace', fontWeight: 600 }}>
                      {rv.numero}
                    </td>
                    <td className="px-5 py-3 font-semibold" style={{ color: 'var(--text)' }}>
                      {rv.entreprise_nom || '—'}
                    </td>
                    <td className="px-5 py-3 text-xs" style={{ color: 'var(--text-secondary)', fontFamily: 'monospace' }}>
                      {rv.bl_origine_numero || '—'}
                    </td>
                    <td className="px-5 py-3 text-xs whitespace-nowrap" style={{ color: 'var(--text-muted)' }}>
                      {fmtDate(rv.date_retour)}
                    </td>
                    <td className="px-5 py-3 text-xs" style={{ color: rv.motif ? 'var(--text-secondary)' : '#CBD5E1', fontStyle: rv.motif ? 'normal' : 'italic', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {rv.motif || 'Aucun motif'}
                    </td>
                    <td className="px-5 py-3 text-xs font-semibold text-right whitespace-nowrap" style={{ color: 'var(--text)' }}>
                      {fmtMt(rv.total_ht)}
                    </td>
                    <td className="px-5 py-3">
                      <StatutBadge statut={rv.statut} />
                    </td>
                    <td className="px-5 py-3">
                      {rv.odoo_refund_name ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#EFF6FF', color: '#1D4ED8', border: '1px solid #BFDBFE', borderRadius: 6, padding: '2px 7px', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' }}>
                          <Receipt size={10} />
                          {rv.odoo_refund_name}
                        </span>
                      ) : '—'}
                    </td>
                    <td className="px-5 py-3 text-xs whitespace-nowrap" style={{ color: 'var(--text-muted)' }}>
                      {(rv.created_by_nom || '').split(' ')[0] || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Panneau latéral */}
      {panel && (
        <SidePanel
          open={!!panel}
          onOpenChange={v => { if (!v) setPanel(null); }}
          title={panel.retour.numero}
          copyValue={panel.retour.numero}
          subtitle={panel.retour.entreprise_nom}
          size="lg"
          headerCenter={
            panel.retour.created_by_nom && (
              <span
                data-testid="rv-affaire-suivie-par"
                style={{ fontSize: '0.72rem', color: 'var(--text-disabled)', fontStyle: 'italic', whiteSpace: 'nowrap' }}>
                Affaire suivie par {panel.retour.created_by_nom}
              </span>
            )
          }>
          <RetourDetailPanel
            retour={panel.retour}
            onClose={() => setPanel(null)}
            onSaved={handleSaved}
          />
        </SidePanel>
      )}

      {/* Modal création */}
      {showModal && <NouveauRetourModal onClose={() => setShowModal(false)} onCreated={handleCreated} />}
    </div>
  );
}
